import {
  createCourseLesson,
  deleteLesson,
  updateLesson,
  updateLessonsOrder,
} from '@/data/services/lessonServices';
import Lesson, { TempLesson } from '@/models/Lesson';
import { buildChangedObject } from '@/utils/buildChangedObject';
import {
  performCreateLessonBooks,
  performUpdateLessonBooks,
} from '@/utils/courseStudio/book';
import { performCreateChapters } from '@/utils/courseStudio/chapter';
import { performUpdateLessonSubchapters } from '@/utils/courseStudio/subchapter';
import { performCreateSubchaptersIntoLessons } from '@/utils/courseStudio/subchapter';
import { performUpdateLessonChapters } from '@/utils/courseStudio/chapter';
import { adapterLessonToLessonDTO } from '@/utils/lesson/adapter';
import { separeArrayStudioActions } from './info/CourseStudioInfo.hooks';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';

async function deleteLessons(slug: string, lessons: Lesson[]) {
  const deletedLessons: Lesson[] = [];

  for (const lesson of lessons) {
    await deleteLesson(lesson.id, slug);
    deletedLessons.push(lesson);
  }

  return deletedLessons;
}

async function performUpdateLesson(
  slug: string,
  newLesson: TempLesson,
  currentLesson: TempLesson,
) {
  const lessonId = newLesson.id;

  const changes = buildChangedObject(currentLesson, newLesson, {
    ignoreFields: ['tempId'],
  });

  if (!Object.keys(changes).length) return currentLesson;

  return await updateLesson(
    { slugCourseName: slug, lessonId },
    adapterLessonToLessonDTO(changes),
  );
}

async function updateLessons(
  slug: string,
  currentLessons: TempLesson[],
  newLessons: TempLesson[],
) {
  const currentLessonsMap = new Map(currentLessons.map(l => [l.id, l]));
  const updatedLessons: TempLesson[] = [];

  for (const lesson of newLessons) {
    const currentLesson = currentLessonsMap.get(lesson.id);

    if (!currentLesson) continue;

    const updated = await performUpdateLesson(slug, lesson, currentLesson);

    updatedLessons.push(Object.assign({}, lesson, updated));
  }

  return updatedLessons;
}

async function createLessons(slug: string, lessons: TempLesson[]) {
  const createdLessons: TempLesson[] = [];

  for (const lesson of lessons) {
    const createdLesson = await createCourseLesson({
      slugCourseName: slug,
      body: adapterLessonToLessonDTO(lesson),
    });

    createdLessons.push(Object.assign({}, lesson, createdLesson));
  }

  return createdLessons;
}

function extractLessonIds(lessons: TempLesson[]): number[] {
  return lessons.filter(lesson => lesson.id).map(lesson => lesson.id);
}

function hasOrderChanged(
  currentLessons: TempLesson[],
  newLessons: TempLesson[],
): boolean {
  const newOrderIds = extractLessonIds(newLessons);
  const currentOrderIds = extractLessonIds(currentLessons);

  if (newOrderIds.length === 0) return false;

  return !newOrderIds.every((id, index) => id === currentOrderIds[index]);
}

function buildLessonsAfterCRUD(
  newLessons: TempLesson[],
  lessonsFromCreate: TempLesson[],
  lessonsFromUpdate: TempLesson[],
): TempLesson[] {
  const updateMap = new Map(lessonsFromUpdate.map(l => [l.id, l]));
  const createMap = new Map(lessonsFromCreate.map(l => [l.tempId, l]));

  return newLessons.map((lesson, index) => {
    const resultLesson = lesson.id
      ? updateMap.get(lesson.id) || lesson
      : createMap.get(lesson.tempId) || lesson;

    resultLesson.order = index + 1;
    return resultLesson;
  });
}

export async function reorderLessons(
  slug: string,
  lessonsToReorder: TempLesson[],
) {
  const lessonsForReorder = lessonsToReorder.filter(lesson => lesson.id);

  const lessonsForReorderWithIds = lessonsForReorder.map(lesson => ({
    id: lesson.id,
  }));

  await updateLessonsOrder(slug, lessonsForReorderWithIds);
}

export function useCourseStudioDirectLessons() {
  async function handleCreateDirectLessons(
    slug: string,
    newLessons: TempLesson[],
  ): Promise<TempLesson[]> {
    const createdLessons: TempLesson[] = await createLessons(slug, newLessons);

    const lessonsWithBooks = await performCreateLessonBooks(createdLessons);
    const lessonsWithChapters = await performCreateChapters(lessonsWithBooks);
    const lessonsWithSubchapters = await performCreateSubchaptersIntoLessons(
      lessonsWithChapters,
    );

    return lessonsWithSubchapters;
  }

  async function performCRUDOperations(
    slug: string,
    currentLessons: TempLesson[],
    newLessonBlocks: TempLessonBlock[],
    newLessons: TempLesson[],
  ) {
    const { arrayToDelete, arrayToUpdate, arrayToCreate } =
      separeArrayStudioActions(currentLessons, newLessons);

    const arrayToDeleteLessons = arrayToDelete.filter(
      lesson =>
        !newLessonBlocks
          .flatMap(block => block.lessons)
          .some(l => l.id === lesson.id),
    );
    await deleteLessons(slug, arrayToDeleteLessons);
    const lessonsFromUpdate = await updateLessons(
      slug,
      currentLessons,
      arrayToUpdate,
    );
    const lessonsFromCreate = await createLessons(slug, arrayToCreate);

    return buildLessonsAfterCRUD(
      newLessons,
      lessonsFromCreate,
      lessonsFromUpdate,
    );
  }

  async function updateBooksAndChapters(
    currentLessons: TempLesson[],
    lessons: TempLesson[],
  ) {
    const lessonsWithBooks = await performUpdateLessonBooks(
      currentLessons,
      lessons,
    );
    const lessonsWithChapters = await performUpdateLessonChapters(
      currentLessons,
      lessonsWithBooks,
    );
    const lessonsWithSubchapters = await performUpdateLessonSubchapters(
      currentLessons,
      lessonsWithChapters,
    );
    return lessonsWithSubchapters;
  }

  async function handleLessonsReordering(
    slug: string,
    currentLessons: TempLesson[],
    updatedLessons: TempLesson[],
  ) {
    const orderChanged = hasOrderChanged(currentLessons, updatedLessons);
    if (orderChanged) {
      await reorderLessons(slug, updatedLessons);
    }
  }

  async function handleUpdateDirectLessons(
    slug: string,
    currentLessons: TempLesson[],
    newLessonBlocks: TempLessonBlock[] = [],
    newLessons: TempLesson[],
  ) {
    const lessonsAfterCRUD = await performCRUDOperations(
      slug,
      currentLessons,
      newLessonBlocks,
      newLessons,
    );

    const lessonsWithUpdatedStructure = await updateBooksAndChapters(
      currentLessons,
      lessonsAfterCRUD,
    );

    await handleLessonsReordering(
      slug,
      currentLessons,
      lessonsWithUpdatedStructure,
    );

    return lessonsWithUpdatedStructure;
  }

  return {
    handleUpdateDirectLessons,
    handleCreateDirectLessons,
  };
}
